import { Component, ViewChild, EventEmitter, Output } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { MerchantService } from '../../services/merchant/merchant.service';

export interface FileUploadResult {
  fileName: string;
  status: 'pending' | 'uploading' | 'pushing' | 'success' | 'error';
  message?: string;
  idealId?: string;
}

@Component({
  selector: 'app-bulk-upload-ideals',
  templateUrl: './bulk-upload-ideals.component.html',
  styleUrls: ['./bulk-upload-ideals.component.scss']
})
export class BulkUploadIdealsComponent {
  @ViewChild('bulkUploadModal', { static: false })
  private bulkUploadModal: NgbModalRef;

  @Output() uploadCompleted = new EventEmitter<void>();

  selectedMerchantGroup: any = null;
  selectedMerchant: any = null;
  selectedTemplate: any = null;
  selectedFiles: File[] = [];
  merchantGroups: any[] = [];
  merchants: any[] = [];
  merchantTemplates: any[] = [];
  isUploading = false;

  uploadResults: FileUploadResult[] = [];
  summaryCount = { success: 0, error: 0, total: 0 };

  get isTiktokMerchant(): boolean {
    if (!this.selectedMerchant) return false;
    const type = this.selectedMerchant.merchantType;
    return type === 2 || type === 6 || type === 7;
  }

  get canUpload(): boolean {
    if (!this.selectedMerchantGroup || this.selectedFiles.length === 0) return false;
    if (this.selectedMerchant && !this.selectedTemplate) return false;
    return true;
  }

  constructor(
    private modalService: NgbModal,
    private toastr: ToastrService,
    private http: HttpClient,
    private merchantService: MerchantService
  ) {
    this.loadMerchantGroups();
  }

  loadMerchantGroups() {
    this.merchantService.getGrantedMerchantGroups().subscribe(
      (response) => {
        this.merchantGroups = response;
      },
      (error) => {
        this.toastr.error('Failed to load merchant groups');
      }
    );
  }

  openBulkUploadModal() {
    this.resetForm();
    this.modalService.open(this.bulkUploadModal, { size: 'lg', backdrop: 'static' });
  }

  onMerchantGroupChange() {
    this.selectedMerchant = null;
    this.selectedTemplate = null;
    this.merchants = [];
    this.merchantTemplates = [];
    if (this.selectedMerchantGroup) {
      this.merchantService.getGrantedMerchants(this.selectedMerchantGroup).subscribe(
        (response) => {
          this.merchants = response;
        }
      );
    }
  }

  onMerchantChange(merchant: any) {
    this.selectedMerchant = merchant;
    this.selectedTemplate = null;
    this.merchantTemplates = [];
    if (merchant) {
      this.merchantService.getTemplates(merchant.merchantId).subscribe(
        (templates) => {
          this.merchantTemplates = templates;
          if (templates && templates.length > 0) {
            const active = templates.find(t => t.isActive);
            this.selectedTemplate = active ? active.id : templates[0].id;
          }
        }
      );
    }
  }

  resetForm() {
    this.selectedMerchantGroup = null;
    this.selectedMerchant = null;
    this.selectedTemplate = null;
    this.selectedFiles = [];
    this.merchants = [];
    this.merchantTemplates = [];
    this.uploadResults = [];
    this.summaryCount = { success: 0, error: 0, total: 0 };
  }

  onFileSelected(event: any) {
    const files = event.target.files as FileList;
    if (files) {
      this.selectedFiles = Array.from(files).filter((file: File) => {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        return validTypes.includes(file.type);
      });
    }
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  async uploadFiles() {
    if (!this.canUpload) return;

    this.isUploading = true;
    this.uploadResults = this.selectedFiles.map(f => ({
      fileName: f.name,
      status: 'pending' as const
    }));
    this.summaryCount = { success: 0, error: 0, total: this.selectedFiles.length };

    const shouldPushToMerchant = this.selectedMerchant && this.selectedTemplate && this.isTiktokMerchant;

    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i];
      this.uploadResults[i].status = 'uploading';

      try {
        const formData = new FormData();
        formData.append('merchantGroupId', this.selectedMerchantGroup);
        formData.append('files', file);

        const uploadResponse: any = await this.http
          .post('api/PreProducts/bulkUploadIdeals', formData)
          .toPromise();

        if (!uploadResponse?.success || !uploadResponse.data?.length) {
          this.uploadResults[i].status = 'error';
          this.uploadResults[i].message = 'Upload failed';
          this.summaryCount.error++;
          continue;
        }

        const idealId = uploadResponse.data[0].id;
        this.uploadResults[i].idealId = idealId;

        if (shouldPushToMerchant) {
          this.uploadResults[i].status = 'pushing';

          const pushData = {
            merchantId: this.selectedMerchant.merchantId,
            templateId: this.selectedTemplate,
            options: { overrideDescription: false, useAIName: false, useAIDescription: false },
            idealIds: [idealId]
          };

          await this.merchantService.addProductToAccountFromIdeal(pushData).toPromise();

          this.uploadResults[i].status = 'success';
          this.uploadResults[i].message = 'Uploaded & pushed to merchant';
        } else {
          this.uploadResults[i].status = 'success';
          this.uploadResults[i].message = 'Uploaded';
        }
        this.summaryCount.success++;
      } catch (error) {
        this.uploadResults[i].status = 'error';
        this.uploadResults[i].message = (error as any)?.error?.message || (error as any)?.message || 'Failed';
        this.summaryCount.error++;
      }
    }

    this.isUploading = false;
    this.uploadCompleted.emit();

    if (this.summaryCount.error === 0) {
      this.toastr.success(`All ${this.summaryCount.total} files processed successfully`);
    } else {
      this.toastr.warning(`${this.summaryCount.success} succeeded, ${this.summaryCount.error} failed`);
    }
  }

  getFileSize(file: File): string {
    const sizeInMB = file.size / (1024 * 1024);
    return sizeInMB.toFixed(2) + ' MB';
  }

  getFileType(file: File): string {
    return file.type.split('/')[1].toUpperCase();
  }
}
